import { HTMLAttributes, ReactNode } from "react";
import { Panel } from "@/components/atoms";
import { TypographyVariant } from "@/components/atoms/Typography/Typography";
import { clsx } from "@/utils";
import { SetupPanelHeader } from "../QuickSetup/common/components/SetupPanelHeader";

export interface StaticSetupPanelProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  stepNumber?: number;
  title: string | React.ReactNode;
  titleVariant?: TypographyVariant;
  description?: string;
  /** Rendered on the right of the header in place of the "N steps" count. */
  badge?: ReactNode;
  stepCount?: number;
  isOpen?: boolean;
  isLocked?: boolean;
  onToggle?: () => void;
  completedSteps?: number;
}

export const StaticSetupPanel = ({
  stepNumber,
  title,
  titleVariant = "title",
  description,
  badge,
  stepCount,
  isOpen = false,
  isLocked = false,
  onToggle,
  completedSteps = 0,
  children,
  className,
  ...props
}: StaticSetupPanelProps) => {
  const showProgressBar = stepCount && completedSteps > 0;
  const progressPercentage = stepCount ? (completedSteps / stepCount) * 100 : 0;

  if (isLocked) {
    return (
      <Panel className={clsx("bg-neutral-gray px-5 py-4", className)} data-panel-step={stepNumber} {...props}>
        <SetupPanelHeader stepNumber={stepNumber} title={title} titleVariant={titleVariant} description={description} isLocked />
      </Panel>
    );
  }

  return (
    <Panel
      className={clsx("!p-0 transition-all duration-200", isOpen ? "!border-vivid-purple shadow-focus-ring-vivid-purple" : "", className)}
      data-panel-step={stepNumber}
      {...props}
    >
      <SetupPanelHeader
        stepNumber={stepNumber}
        title={title}
        titleVariant={titleVariant}
        description={description}
        badge={badge}
        stepCount={stepCount}
        isOpen={isOpen}
        isCompleted={completedSteps === stepCount && stepCount !== 0}
        showProgressBar={!!showProgressBar}
        progressPercentage={progressPercentage}
        onClick={onToggle}
      />

      <div
        className={clsx("grid transition-[grid-template-rows] duration-200 ease-in-out", isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-3 p-4">{children}</div>
        </div>
      </div>
    </Panel>
  );
};
